import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { MetricChartSpec } from '@/components/charts/generic';
import { MetricChart, useMetricChart } from '@/components/charts/generic';
import { useChartTheme } from '@/components/charts/theme';
import type {
    FeedingDashboardConfig,
    FeedingFallthroughDay,
    FeedingResolution,
} from '@/domain/feedingDashboard';

import { pelletSurfaceQuality } from './bioscopeQuality';
import { dashboardLocale } from './dashboardLocale';
import { FeedingChartPanel } from './FeedingChartPanel/FeedingChartPanel';
import { fallthroughTimelinePoints } from './fallthroughTimeline';
import { FEEDING_COLORS, WARNED_BAR_OUTLINE_WIDTH, warningPalette } from './feedingChartColors';
import { PenChartKey } from './PenChartKey';
import { partialWeeks } from './partialWeeks';
import {
    fallthroughColor,
    fallthroughOutline,
    fallthroughThresholdLines,
    isPelletBanded,
} from './pelletBandColors';
import { todayMarker } from './todayMarker';

import styles from './FallthroughCharts.module.scss';

interface FallthroughDailyChartProps {
    days: FeedingFallthroughDay[];
    config: FeedingDashboardConfig;
    /** Bucket granularity, so a long period reads the same here as on the feed graph. */
    resolution: FeedingResolution;
    /** The site's day in progress, marked on the chart; null from an older backend. */
    siteToday: string | null;
    /** The selected period start, so a weekly chart can say which weeks it cuts short. */
    periodStart: string;
    /** The selected period end: a period that ends before `siteToday` marks nothing. */
    periodEnd: string;
    loading?: boolean;
    error?: unknown;
}

/**
 * Pellet fallthrough over time (IPS 27, 2.7.2), one bar per bucket of the period at
 * the overview's resolution, as the average across all operators on duty. Deliberately
 * not split by operator: the metric reflects the pen or site, not an individual
 * operator's performance.
 */
export function FallthroughDailyChart(props: FallthroughDailyChartProps) {
    const { days, config, resolution, siteToday, periodStart, periodEnd, loading, error } = props;
    const { t, i18n } = useTranslation();
    const { fallthroughWarnPct: warnPct, fallthroughAlertPct: alertPct } = config;
    // Read once per mount: the tokens are static for a page load, and reading computed
    // style on every render would force a style recalculation each time.
    // Re-derived when the theme switches; the outline ink differs between themes.
    const chartTheme = useChartTheme();
    const palette = useMemo(() => warningPalette(chartTheme), [chartTheme]);

    const points = fallthroughTimelinePoints(days, resolution);
    const buckets = points.map((point) => point.day);
    const today = todayMarker({ siteToday, periodEnd, resolution, buckets }, t);
    const partial = partialWeeks({ periodStart, periodEnd, siteToday, resolution, buckets }, t);
    const hasValue = points.some((point) => point.value != null);
    // The dot rates pellet data alone; see pelletSurfaceQuality.
    const quality = pelletSurfaceQuality(hasValue);
    const banded = points.some((point) => isPelletBanded(point.value, warnPct, alertPct));

    const spec: MetricChartSpec<'bar'> = {
        kind: 'bar',
        days: buckets,
        // Same reason as the feed/weight chart: a weekly bucket is keyed by its Monday,
        // so the day axis would print one date for a seven-day bar.
        xAxisKind: resolution === 'week' ? 'week' : 'day',
        locale: dashboardLocale(i18n),
        series: [
            {
                key: 'fallthrough',
                label: t('feedingModule.kpi.fallthrough'),
                color: FEEDING_COLORS.bioscope,
                // The only series on the chart. useMetricChart refuses to hide the last
                // series carrying a legend entry, so a legend here renders a control
                // that does nothing when clicked. The axis title already names it.
                showInLegend: false,
                data: points,
                barColors: points.map((point) =>
                    fallthroughColor(point.value, warnPct, alertPct, palette)
                ),
                barOutlines: {
                    colors: points.map((point) =>
                        fallthroughOutline(point.value, warnPct, alertPct, palette)
                    ),
                    width: WARNED_BAR_OUTLINE_WIDTH,
                },
                decimals: 1,
            },
        ],
        left: {
            label: t('feedingModule.kpi.fallthrough'),
            unit: '%',
            beginAtZero: true,
            valueDecimals: 1,
        },
        referenceLines: fallthroughThresholdLines(warnPct, alertPct, palette),
        tooltipExtraLines: partial.tooltipExtraLines,
        inProgress: today?.inProgress,
    };

    const chart = useMetricChart(spec);
    // Neutral, because a long period turns the chart weekly (statement 4) and a title
    // saying "daily" would contradict the Weekly badge beside it.
    const title = t('feedingModule.charts.fallthroughTimelineTitle');

    return (
        <FeedingChartPanel
            title={title}
            quality={quality}
            qualitySubject="pellet"
            badge={resolution === 'week' ? t('feedingModule.charts.weeklyBadge') : undefined}>
            <MetricChart
                kind="bar"
                data={chart.data}
                options={chart.options}
                legendItems={chart.legendItems}
                visibleSeries={chart.visibleSeries}
                onToggleSeries={chart.toggleSeries}
                chartRef={chart.chartRef}
                isLoading={loading}
                error={error}
                // Shown rather than hiding the panel: on Bioscope 3.0 the camera is
                // wired up, so silence is a data gap to report, and hiding the panel
                // would take the data-quality dot (IPS req 11) with it.
                noDataMessage={t('feedingModule.charts.fallthroughNoData')}
            />
            {/* The threshold lines carry no label in the plot, so the key names them. */}
            {hasValue ? (
                <PenChartKey
                    kind="pellet"
                    warnPct={warnPct}
                    alertPct={alertPct}
                    palette={palette}
                    banded={banded}
                />
            ) : null}
            {resolution === 'week' ? (
                <p className={styles.bucketNotice}>
                    {t('feedingModule.charts.weeklyBucketsWithPellet')}
                </p>
            ) : null}
            {partial.caption ? <p className={styles.bucketNotice}>{partial.caption}</p> : null}
            {today?.caption ? <p className={styles.bucketNotice}>{today.caption}</p> : null}
        </FeedingChartPanel>
    );
}
